// externals

import { gunzipSync, gzipSync, strFromU8, strToU8 } from 'fflate';

/**
 * Generic compressed-payload codec.
 *
 * Turns any JSON-serialisable value into a self-describing, base64-encoded gzip envelope and back
 * again. It deliberately knows nothing about what it is carrying — no payment grids, no cells, no
 * baggage items. Callers that need domain meaning (naming, freshness, per-scenario resolution)
 * build it on top; see `dr-platform-shared`.
 *
 * See DEP F302921 (dr-dash `docs/dep/F302921-PaymentGridBaggageCompression.md`).
 *
 * gzip is used because it is the only codec available synchronously and identically in the browser
 * and in Node, and the result is base64-encoded because the value travels inside a JSON body,
 * which cannot carry raw bytes.
 */

export type CompressionCodec = 'gzip';

/**
 * Self-describing envelope around compressed bytes.
 *
 * `codec` is retained so a future codec change is detected rather than fed to the wrong
 * decompressor; readers treat an unrecognised codec as absent and fall back.
 */
export type CompressedPayload = {
    codec: CompressionCodec;
    enc: 'base64';
    /** base64 of gzip(JSON.stringify(value)) */
    data: string;
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/**
 * `btoa`/`atob` are used rather than `Buffer` so this helper stays runtime-agnostic — it is
 * consumed by browser bundles as well as by Node, and this package deliberately carries no Node
 * type dependency. Both are available in every browser and in Node >= 16.
 */
const toBase64 = (bytes: Uint8Array): string => {
    // Build a latin1 string in chunks; passing the whole array to String.fromCharCode
    // blows the call stack on large payloads.
    let binary = '';
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
    }
    return btoa(binary);
};

const fromBase64 = (value: string): Uint8Array => {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
};

/**
 * Narrows an unknown value to a compressed payload envelope. Does not validate the compressed
 * bytes — `decompressPayload` handles corrupt data.
 */
export const isCompressedPayload = (value: unknown): value is CompressedPayload => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<CompressedPayload>;
    return candidate.codec === 'gzip' && candidate.enc === 'base64' && isNonEmptyString(candidate.data);
};

/**
 * Compresses a JSON-serialisable value into the storage envelope.
 *
 * Returns `undefined` rather than throwing when the value cannot be serialised or compressed, so a
 * caller can skip persistence and let the consumer fall back.
 */
export const compressPayload = (value: unknown): CompressedPayload | undefined => {
    if (value === undefined) return undefined;
    try {
        const json = JSON.stringify(value);
        if (!isNonEmptyString(json)) return undefined;
        // `mtime: 0` keeps the output deterministic; fflate otherwise stamps the current time into
        // the gzip header, so the same value compressed twice would not compare equal and any
        // caller deduping or memoizing on the envelope would see a spurious change every call.
        //
        // Level 6, not 9. This runs synchronously on the browser's main thread on every save, over as
        // much as three payment grids, so the stall is user-visible. Level 9 costs several times level
        // 6 in CPU for one or two percent on JSON of this shape — not a trade worth making when the
        // size budget it would buy back has room to spare.
        const compressed = gzipSync(strToU8(json), { level: 6, mtime: 0 });
        return {
            codec: 'gzip',
            enc: 'base64',
            data: toBase64(compressed)
        };
    } catch (error) {
        return undefined;
    }
};

/**
 * Reverses `compressPayload`.
 *
 * Returns `undefined` for anything that cannot be trusted — a missing envelope, an unrecognised
 * codec, malformed base64, corrupt gzip bytes, or invalid JSON — so every failure mode routes the
 * caller to its fallback instead of surfacing an error. Never throws.
 */
export const decompressPayload = <T = unknown>(value: unknown): T | undefined => {
    if (!isCompressedPayload(value)) return undefined;
    try {
        const json = strFromU8(gunzipSync(fromBase64(value.data)));
        return JSON.parse(json) as T;
    } catch (error) {
        return undefined;
    }
};

